import { css } from '@emotion/react';
import { getEffectiveScanValueAsNumber, ScanGroupOptimized } from '@kaiizen/kai-scan-template-system';
import { ReactNode } from 'react';

import { useEditableGroupLabel } from '@/Client/Lib';
import { groupNamePresenter } from '@/Client/Presenters';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '../Styles';
import { GroupLabelTagIcon } from './StyledIcons/GroupLabelTagIcon';
import { RoomMeasurementsPencilIcon } from './StyledIcons/RoomMeasurementsPencilIcon';

const TASK_HEADER_CONTAINER_STYLE = css`
  label: TASK_HEADER_CONTAINER_STYLE;
  position: sticky;
  top: 0;
  z-index: 20;
`;

const TASKS_HEADER_STYLE = css`
  label: TASKS_HEADER_STYLE;
  display: flex;
  height: ${LAYOUT2.spacing5}px;
  gap: ${LAYOUT2.spacing1}px;
  padding: 0 ${LAYOUT2.spacing1}px 0 ${LAYOUT2.spacing2}px;
  background: ${COLORS2.alwaysDark.background.dark};
  align-items: center;
`;

const TASKS_HEADER_LABEL_REGION_STYLE = css`
  label: TASKS_HEADER_LABEL_REGION_STYLE;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
`;

const TASKS_HEADER_GROUP_NAME_TRIGGER_STYLE = css`
  label: TASKS_HEADER_GROUP_NAME_TRIGGER_STYLE;
  display: inline-flex;
  align-items: center;
  gap: ${LAYOUT2.spacingHalf}px;
  margin: 0;
  padding: 0 ${LAYOUT2.spacingHalf}px;
  border: none;
  background: transparent;
  cursor: pointer;
  max-width: 100%;
  min-width: 0;
  text-align: left;
`;

const TASKS_HEADER_GROUP_NAME_READONLY_STYLE = css`
  label: TASKS_HEADER_GROUP_NAME_READONLY_STYLE;
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  min-width: 0;
`;

const TASKS_HEADER_GROUP_NAME_TEXT_STYLE = css`
  label: TASKS_HEADER_GROUP_NAME_TEXT_STYLE;
  ${TYPOGRAPHY2.mediumBold}
  line-height: 1.125;
  letter-spacing: 0;
  vertical-align: middle;
  color: ${COLORS2.alwaysDark.text.white};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TASKS_HEADER_GROUP_LABEL_EDIT_COMPOSITE_STYLE = css`
  label: TASKS_HEADER_GROUP_LABEL_EDIT_COMPOSITE_STYLE;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2px;
  flex: 0 1 auto;
  min-width: 8rem;
  max-width: min(100%, 20rem);
  background: ${COLORS2.alwaysDark.background.dark};
  border: 1px solid var(--border2);
  border-radius: 4px;
  padding: ${LAYOUT2.spacingHalf}px ${LAYOUT2.spacing1}px;
  outline: none;
  &:focus-within {
    border-color: ${COLORS2.alwaysDark.text.green};
  }
`;

const TASKS_HEADER_GROUP_LABEL_EDIT_PREFIX_WRAP_STYLE = css`
  label: TASKS_HEADER_GROUP_LABEL_EDIT_PREFIX_WRAP_STYLE;
  flex-shrink: 1;
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const TASKS_HEADER_GROUP_LABEL_EDIT_PREFIX_TEXT_STYLE = css`
  label: TASKS_HEADER_GROUP_LABEL_EDIT_PREFIX_TEXT_STYLE;
  ${TYPOGRAPHY2.mediumBold}
  line-height: 1.125;
  letter-spacing: 0;
  color: ${COLORS2.alwaysDark.text.white};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1 1 auto;
  min-width: 0;
`;

const TASKS_HEADER_GROUP_LABEL_EDIT_OPEN_PAREN_STYLE = css`
  label: TASKS_HEADER_GROUP_LABEL_EDIT_OPEN_PAREN_STYLE;
  ${TYPOGRAPHY2.mediumBold}
  line-height: 1.125;
  color: ${COLORS2.alwaysDark.text.white};
  flex-shrink: 0;
`;

const TASKS_HEADER_GROUP_LABEL_EDIT_SUFFIX_FIELD_STYLE = css`
  label: TASKS_HEADER_GROUP_LABEL_EDIT_SUFFIX_FIELD_STYLE;
  flex: 1 1 auto;
  flex-grow: 1;
  min-width: 48px;
  ${TYPOGRAPHY2.mediumBold}
  line-height: 1.125;
  letter-spacing: 0;
  color: ${COLORS2.alwaysDark.text.white};
  background: transparent;
  border: none;
  margin: 0;
  padding: 0 2px;
  outline: none;
`;

const TASKS_HEADER_GROUP_LABEL_EDIT_CLOSING_PAREN_STYLE = css`
  label: TASKS_HEADER_GROUP_LABEL_EDIT_CLOSING_PAREN_STYLE;
  ${TYPOGRAPHY2.mediumBold}
  line-height: 1.125;
  color: ${COLORS2.alwaysDark.text.white};
  flex-shrink: 0;
  margin-left: 2px;
`;

const TASKS_HEADER_ACTIONS_STYLE = css`
  label: TASKS_HEADER_ACTIONS_STYLE;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;
  margin-left: auto;
`;

/** Tighter horizontal rhythm between pencil, notes, and add (+) than the default actions row gap. */
const TASKS_HEADER_RIGHT_CLUSTER_STYLE = css`
  label: TASKS_HEADER_RIGHT_CLUSTER_STYLE;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacingHalf}px;
  flex-shrink: 0;
  min-width: 0;
`;

const TASKS_HEADER_COST_ROW_STYLE = css`
  label: TASKS_HEADER_COST_ROW_STYLE;
  display: flex;
  gap: ${LAYOUT2.spacing * .5}px;
  align-items: flex-end;
  margin-right: ${LAYOUT2.spacing1}px;
`;

const TASKS_HEADER_COST_LABEL_STYLE = css`
  label: TASKS_HEADER_COST_LABEL_STYLE;
  ${TYPOGRAPHY2.medium}
  color: ${COLORS2.alwaysDark.text.white};
  padding-bottom: 1px;
`;

const TASKS_HEADER_COST_VALUE_STYLE = css`
  label: TASKS_HEADER_COST_VALUE_STYLE;
  ${TYPOGRAPHY2.largeBold}
  color: ${COLORS2.alwaysDark.text.green};
`;

const ROOM_MEASUREMENTS_TASKS_HEADER_TRIGGER_STYLE = css`
  label: ROOM_MEASUREMENTS_TASKS_HEADER_TRIGGER_STYLE;
  display: inline-flex;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  ${TYPOGRAPHY2.medium}
  color: ${COLORS2.alwaysDark.text.white};
`;

const ROOM_MEASUREMENTS_INLINE_SUMMARY_STYLE = css`
  label: ROOM_MEASUREMENTS_INLINE_SUMMARY_STYLE;
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${LAYOUT2.spacing2}px;
  max-width: 100%;
`;

const ROOM_MEASUREMENTS_STAT_GROUP_STYLE = css`
  label: ROOM_MEASUREMENTS_STAT_GROUP_STYLE;
  display: inline-flex;
  align-items: baseline;
  gap: ${LAYOUT2.spacingHalf}px;
`;

const ROOM_MEASUREMENTS_STAT_LABEL_STYLE = css`
  label: ROOM_MEASUREMENTS_STAT_LABEL_STYLE;
  ${TYPOGRAPHY2.small}
  color: ${COLORS2.alwaysDark.text.white};
`;

const ROOM_MEASUREMENTS_STAT_VALUE_STYLE = css`
  label: ROOM_MEASUREMENTS_STAT_VALUE_STYLE;
  ${TYPOGRAPHY2.mediumBold}
  color: ${COLORS2.alwaysDark.text.white};
`;

const ROOM_MEASUREMENTS_STAT_UNIT_STYLE = css`
  label: ROOM_MEASUREMENTS_STAT_UNIT_STYLE;
  ${TYPOGRAPHY2.xSmall}
  color: ${COLORS2.alwaysDark.text.white};
`;

const TasksHeaderGroupLabelControl = ({
  commitCustomLabel,
  group,
  scanIsEditable,
}: {
  commitCustomLabel: (customLabel: string) => void;
  group: ScanGroupOptimized;
  scanIsEditable: boolean;
}) => {
  const templateName = groupNamePresenter(group);
  const { beginEditing, cancelOrCommitOnKey, commitUnlessJustDismissed, draftSuffix, editing, updateDraftSuffix } = useEditableGroupLabel({
    commitCustomLabel,
    group,
    scanIsEditable,
  });

  if (editing) {
    return (
      <div css={TASKS_HEADER_GROUP_LABEL_EDIT_COMPOSITE_STYLE}>
        <div css={TASKS_HEADER_GROUP_LABEL_EDIT_PREFIX_WRAP_STYLE}>
          <span css={TASKS_HEADER_GROUP_LABEL_EDIT_PREFIX_TEXT_STYLE} title={templateName}>
            {templateName}
          </span>
          <span css={TASKS_HEADER_GROUP_LABEL_EDIT_OPEN_PAREN_STYLE}>{' ('}</span>
        </div>
        <input
          aria-label='Custom group label'
          autoFocus
          css={TASKS_HEADER_GROUP_LABEL_EDIT_SUFFIX_FIELD_STYLE}
          placeholder='label'
          value={draftSuffix}
          tabIndex={1}
          onBlur={commitUnlessJustDismissed}
          onChange={event => updateDraftSuffix(event.target.value)}
          onKeyDown={cancelOrCommitOnKey}
        />
        <span css={TASKS_HEADER_GROUP_LABEL_EDIT_CLOSING_PAREN_STYLE}>)</span>
      </div>
    );
  }

  if (!scanIsEditable) {
    return (
      <div css={TASKS_HEADER_GROUP_NAME_READONLY_STYLE}>
        <span css={TASKS_HEADER_GROUP_NAME_TEXT_STYLE}>{groupNamePresenter(group)}</span>
      </div>
    );
  }

  return (
    <button
      type='button'
      title='Add a label to the group'
      css={TASKS_HEADER_GROUP_NAME_TRIGGER_STYLE}
      tabIndex={1}
      onClick={beginEditing}
    >
      <span css={TASKS_HEADER_GROUP_NAME_TEXT_STYLE}>{groupNamePresenter(group)}</span>
      <GroupLabelTagIcon />
    </button>
  );
};

const formatRoomMeasurementHeaderValue = (value: number | undefined) => value == null || Number.isNaN(value) ? '—' : value.toFixed(1);

const RoomMeasurementsHeaderUnit = ({ unit }: { unit: string }) => <span css={ROOM_MEASUREMENTS_STAT_UNIT_STYLE}>{unit}</span>;

const RoomMeasurementsTasksHeaderTrigger = ({
  group,
  onOpenModal,
}: {
  group: ScanGroupOptimized;
  onOpenModal: () => void;
}) => {
  const floorAreaValue = getEffectiveScanValueAsNumber(group.values?.roomArea);
  const perimeterValue = getEffectiveScanValueAsNumber(group.values?.roomPerimeter);
  const wallAreaValue = getEffectiveScanValueAsNumber(group.values?.roomWallArea);
  const hasPersistedRoomMeasurements = floorAreaValue != null || perimeterValue != null || wallAreaValue != null;

  return (
    <button
      type='button'
      css={ROOM_MEASUREMENTS_TASKS_HEADER_TRIGGER_STYLE}
      onClick={onOpenModal}
      aria-label='Edit room measurements'
    >
      {hasPersistedRoomMeasurements
        ? (
          <>
            <span css={ROOM_MEASUREMENTS_INLINE_SUMMARY_STYLE}>
              <span css={ROOM_MEASUREMENTS_STAT_GROUP_STYLE}>
                <span css={ROOM_MEASUREMENTS_STAT_LABEL_STYLE}>Floor</span>
                <span css={ROOM_MEASUREMENTS_STAT_VALUE_STYLE}>{formatRoomMeasurementHeaderValue(floorAreaValue)}</span>
                <RoomMeasurementsHeaderUnit unit='sqft' />
              </span>
              <span css={ROOM_MEASUREMENTS_STAT_GROUP_STYLE}>
                <span css={ROOM_MEASUREMENTS_STAT_LABEL_STYLE}>Perimeter</span>
                <span css={ROOM_MEASUREMENTS_STAT_VALUE_STYLE}>{formatRoomMeasurementHeaderValue(perimeterValue)}</span>
                <RoomMeasurementsHeaderUnit unit='ft' />
              </span>
              <span css={ROOM_MEASUREMENTS_STAT_GROUP_STYLE}>
                <span css={ROOM_MEASUREMENTS_STAT_LABEL_STYLE}>Walls</span>
                <span css={ROOM_MEASUREMENTS_STAT_VALUE_STYLE}>{formatRoomMeasurementHeaderValue(wallAreaValue)}</span>
                <RoomMeasurementsHeaderUnit unit='sqft' />
              </span>
            </span>
            <RoomMeasurementsPencilIcon />
          </>
        )
        : (
          <>
            Measurements
            <RoomMeasurementsPencilIcon />
          </>
        )}
    </button>
  );
};

export const ScanEditorPageMeasurementTitleBar = ({
  buttons,
  costFormatted,
  group,
  onCommitCustomLabel,
  onOpenMeasurementsModal,
  scanIsEditable,
}: {
  buttons?: ReactNode[];
  costFormatted: string;
  group: ScanGroupOptimized;
  onCommitCustomLabel: (trimmedLabel: string) => void;
  onOpenMeasurementsModal: () => void;
  scanIsEditable: boolean;
}) => (
  <div css={TASK_HEADER_CONTAINER_STYLE}>
    <div css={TASKS_HEADER_STYLE}>
      <div css={TASKS_HEADER_LABEL_REGION_STYLE}>
        <TasksHeaderGroupLabelControl
          commitCustomLabel={onCommitCustomLabel}
          group={group}
          scanIsEditable={scanIsEditable}
        />
      </div>
      <div css={TASKS_HEADER_ACTIONS_STYLE}>
        <div css={TASKS_HEADER_COST_ROW_STYLE}>
          <div css={TASKS_HEADER_COST_LABEL_STYLE}>Cost:</div>
          <div css={TASKS_HEADER_COST_VALUE_STYLE}>{costFormatted}</div>
        </div>
        <div css={TASKS_HEADER_RIGHT_CLUSTER_STYLE}>
          <RoomMeasurementsTasksHeaderTrigger
            group={group}
            onOpenModal={onOpenMeasurementsModal}
          />
          {buttons}
        </div>
      </div>
    </div>
  </div>
);
